import { createTeam, formationAnchor } from "./formations";
import type { TacticalPresetId } from "../domain/club";
import {
  EMPTY_INPUT,
  PITCH,
  type BallState,
  type MatchConfig,
  type MatchEvent,
  type MatchInput,
  type MatchLineupPlayer,
  type MatchMentality,
  type MatchSnapshot,
  type PlayerState,
  type TeamId
} from "./types";

const SIMULATION_STEP_SECONDS = 1 / 60;
const PLAYER_CONTROL_RADIUS = 1.05;
const BALL_RADIUS = 0.11;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function length(x: number, z: number): number {
  return Math.hypot(x, z);
}

function normalize(x: number, z: number): { x: number; z: number } {
  const magnitude = length(x, z);
  return magnitude > 0.0001 ? { x: x / magnitude, z: z / magnitude } : { x: 0, z: 0 };
}

function freshStats(): MatchSnapshot["stats"] {
  return {
    homeShots: 0,
    awayShots: 0,
    homeShotsOnTarget: 0,
    awayShotsOnTarget: 0,
    homePossessionTicks: 0,
    awayPossessionTicks: 0,
    homePasses: 0,
    awayPasses: 0,
    homeTackles: 0,
    awayTackles: 0
  };
}

function freshBall(): BallState {
  return {
    x: 0,
    y: BALL_RADIUS,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    angularVelocityX: 0,
    angularVelocityZ: 0,
    controlledBy: "home-10",
    lastTouch: "home-10"
  };
}

export class MatchSimulation {
  static readonly stepSeconds = SIMULATION_STEP_SECONDS;
  private state: MatchSnapshot;
  private aiDecisionCooldown = 0;
  private previousInput: MatchInput = { ...EMPTY_INPUT };
  private secondDefenderActive = false;

  constructor(config: MatchConfig, restored?: MatchSnapshot) {
    if (restored) {
      this.state = {
        ...structuredClone(restored),
        mentality: restored.mentality ?? "balanced",
        substitutions: restored.substitutions ?? []
      };
      return;
    }

    this.state = {
      schemaVersion: 1,
      config,
      phase: "presentation",
      clockSeconds: 0,
      homeScore: 0,
      awayScore: 0,
      players: [
        ...createTeam("home", config.homeFormation ?? "4-3-3", config.homeLineup),
        ...createTeam("away", config.awayFormation ?? "4-3-3")
      ],
      ball: freshBall(),
      activePlayerId: "home-10",
      stats: freshStats(),
      events: [],
      eventNonce: 0,
      mentality: "balanced",
      substitutions: []
    };
    this.addEvent("kickoff", null, null, "Match ready");
  }

  startFirstHalf(): void {
    if (this.state.phase !== "presentation") return;
    this.state.phase = "first-half";
    this.addEvent("kickoff", "home", null, "First half");
  }

  startSecondHalf(): void {
    if (this.state.phase !== "halftime") return;
    this.resetKickoff("away");
    this.state.phase = "second-half";
    this.addEvent("kickoff", "away", null, "Second half");
  }

  snapshot(): MatchSnapshot {
    return structuredClone(this.state);
  }

  setMentality(mentality: MatchMentality): void {
    if (this.state.mentality === mentality) return;
    this.state.mentality = mentality;
    const label =
      mentality === "attacking"
        ? "Attack shape"
        : mentality === "defensive"
          ? "Protect shape"
          : "Balanced shape";
    this.addEvent("tactics", "home", null, label);
  }

  substitutePlayer(outPlayerId: string, incoming: MatchLineupPlayer): boolean {
    if (this.state.phase === "fulltime" || this.state.substitutions.length >= 3) return false;
    const outgoing = this.player(outPlayerId);
    if (!outgoing || outgoing.team !== "home" || outgoing.role === "GK") return false;
    if (this.state.players.some((player) => player.profileId === incoming.profileId)) return false;
    if (!this.state.config.homeBench?.some((player) => player.profileId === incoming.profileId)) {
      return false;
    }

    const outProfileId = outgoing.profileId ?? outgoing.id;
    const outName = outgoing.displayName ?? `Player ${outgoing.shirtNumber}`;
    const outNumber = outgoing.shirtNumber;
    outgoing.profileId = incoming.profileId;
    outgoing.displayName = incoming.displayName;
    outgoing.shirtNumber = incoming.number;
    outgoing.stamina = 1;
    outgoing.action = "idle";
    outgoing.actionNonce += 1;
    const slotIndex = Number(outgoing.id.split("-")[1]) - 1;
    if (this.state.config.homeLineup && slotIndex >= 0) {
      this.state.config.homeLineup[slotIndex] = { ...incoming };
    }
    this.state.config.homeBench = this.state.config.homeBench.map((player) =>
      player.profileId === incoming.profileId
        ? { profileId: outProfileId, displayName: outName, number: outNumber }
        : player
    );
    this.state.substitutions.push({
      minute: Math.min(90, Math.floor(this.state.clockSeconds / 60)),
      outProfileId,
      outName,
      inProfileId: incoming.profileId,
      inName: incoming.displayName,
      slotId: outgoing.id
    });
    this.addEvent(
      "substitution",
      "home",
      outgoing.id,
      `${incoming.displayName} on · ${outName} off`
    );
    return true;
  }

  tick(stepSeconds: number, input: MatchInput): void {
    if (this.state.phase !== "first-half" && this.state.phase !== "second-half") {
      this.previousInput = { ...input };
      return;
    }

    const dt = clamp(stepSeconds, 0, 0.05);
    this.secondDefenderActive = input.secondDefender;
    const clockScale = 5400 / Math.max(1, this.state.config.realDurationSeconds);
    this.state.clockSeconds += dt * clockScale;

    if (this.state.phase === "first-half" && this.state.clockSeconds >= 2700) {
      this.state.clockSeconds = 2700;
      this.state.phase = "halftime";
      this.addEvent("halftime", null, null, "Half-time");
      this.previousInput = { ...input };
      return;
    }

    if (this.state.phase === "second-half" && this.state.clockSeconds >= 5400) {
      this.state.clockSeconds = 5400;
      this.state.phase = "fulltime";
      this.addEvent("fulltime", null, null, "Full-time");
      this.previousInput = { ...input };
      return;
    }

    if (input.switchPlayer && !this.previousInput.switchPlayer) {
      this.switchActivePlayer();
    }

    this.updatePlayers(dt, input);
    this.handleUserActions(input);
    this.updateBall(dt);
    this.resolvePossession();
    this.checkGoal();
    this.previousInput = { ...input };
  }

  private updatePlayers(dt: number, input: MatchInput): void {
    const active = this.player(this.state.activePlayerId);
    const inputDirection = normalize(input.moveX, input.moveZ);
    const inputMagnitude = clamp(length(input.moveX, input.moveZ), 0, 1);

    for (const player of this.state.players) {
      if (player.id === active?.id) {
        const maximumSpeed = input.sprint ? 7.35 : 5.4;
        const targetVx = inputDirection.x * maximumSpeed * inputMagnitude;
        const targetVz = inputDirection.z * maximumSpeed * inputMagnitude;
        const acceleration = input.sprint ? 15 : 19;
        player.vx += clamp(targetVx - player.vx, -acceleration * dt, acceleration * dt);
        player.vz += clamp(targetVz - player.vz, -acceleration * dt, acceleration * dt);
        if (inputMagnitude > 0.08) {
          player.heading = Math.atan2(inputDirection.z, inputDirection.x);
        }
        player.stamina = clamp(
          player.stamina + (input.sprint && inputMagnitude > 0.4 ? -0.02 : 0.008) * dt,
          0.2,
          1
        );
      } else {
        this.updateAiPlayer(player, dt);
      }

      player.x = clamp(player.x + player.vx * dt, -PITCH.halfLength + 0.4, PITCH.halfLength - 0.4);
      player.z = clamp(player.z + player.vz * dt, -PITCH.halfWidth + 0.4, PITCH.halfWidth - 0.4);
      player.action = length(player.vx, player.vz) > 0.35 ? "run" : "idle";
    }
  }

  private updateAiPlayer(player: PlayerState, dt: number): void {
    const owner = this.state.ball.controlledBy ? this.player(this.state.ball.controlledBy) : null;
    const formationId =
      player.team === "home"
        ? this.state.config.homeFormation ?? "4-3-3"
        : this.state.config.awayFormation ?? "4-3-3";
    const anchor = formationAnchor(player, formationId);
    const tacticalPreset: TacticalPresetId =
      player.team === "home" ? this.state.config.homeTacticalPreset ?? "measured" : "measured";
    const tuning = {
      measured: { shift: 0.22, baseSpeed: 4.2, chaseSpeed: 6, carrySpeed: 5.7, depth: 0 },
      vertical: { shift: 0.3, baseSpeed: 4.7, chaseSpeed: 6.25, carrySpeed: 6.1, depth: 2.5 },
      control: { shift: 0.16, baseSpeed: 3.95, chaseSpeed: 5.7, carrySpeed: 5.35, depth: 0.5 },
      shield: { shift: 0.12, baseSpeed: 3.8, chaseSpeed: 5.4, carrySpeed: 5.15, depth: -4 }
    }[tacticalPreset];
    const mentality =
      player.team === "home"
        ? {
            defensive: { depth: -4.5, shift: 0.82, press: 0.92 },
            balanced: { depth: 0, shift: 1, press: 1 },
            attacking: { depth: 4.8, shift: 1.15, press: 1.08 }
          }[this.state.mentality]
        : { depth: 0, shift: 1, press: 1 };
    const ballShiftX = clamp(this.state.ball.x * tuning.shift * mentality.shift, -15, 15);
    const teamDirection = player.team === "home" ? 1 : -1;
    let targetX =
      anchor.x + ballShiftX + (tuning.depth + mentality.depth) * teamDirection;
    let targetZ = anchor.z + this.state.ball.z * 0.08;
    let speed = tuning.baseSpeed;

    const pressers = this.nearestPlayersToBall(
      player.team,
      player.team === "home" && this.secondDefenderActive ? 2 : 1
    );
    if (pressers.some((candidate) => candidate.id === player.id) && (!owner || owner.team !== player.team)) {
      targetX = this.state.ball.x;
      targetZ = this.state.ball.z;
      speed = tuning.chaseSpeed * mentality.press;
    } else if (owner?.id === player.id) {
      targetX = teamDirection * (PITCH.halfLength - 5);
      targetZ = clamp(-player.z * 0.08, -8, 8);
      speed = tuning.carrySpeed;
      this.aiDecisionCooldown -= dt;
      if (this.aiDecisionCooldown <= 0 && Math.abs(player.x) > 38) {
        this.kickBall(player, "shot", {
          x: teamDirection,
          z: clamp(-player.z / 25, -0.35, 0.35)
        });
        this.aiDecisionCooldown = 1.2;
      }
    }

    const direction = normalize(targetX - player.x, targetZ - player.z);
    const distance = length(targetX - player.x, targetZ - player.z);
    const desiredSpeed = distance < 0.6 ? 0 : Math.min(speed, distance * 2);
    const targetVx = direction.x * desiredSpeed;
    const targetVz = direction.z * desiredSpeed;
    player.vx += clamp(targetVx - player.vx, -10 * dt, 10 * dt);
    player.vz += clamp(targetVz - player.vz, -10 * dt, 10 * dt);
    if (desiredSpeed > 0.2) player.heading = Math.atan2(direction.z, direction.x);
  }

  private handleUserActions(input: MatchInput): void {
    const player = this.player(this.state.activePlayerId);
    if (!player) return;

    if (input.pass && !this.previousInput.pass && this.state.ball.controlledBy === player.id) {
      const inputDirection = normalize(input.moveX, input.moveZ);
      const direction =
        length(inputDirection.x, inputDirection.z) > 0
          ? inputDirection
          : { x: Math.cos(player.heading), z: Math.sin(player.heading) };
      this.kickBall(player, "pass", direction, input.passPower);
    }

    if (
      input.through &&
      !this.previousInput.through &&
      this.state.ball.controlledBy === player.id
    ) {
      const inputDirection = normalize(input.moveX, input.moveZ);
      const forward = player.team === "home" ? 1 : -1;
      const direction =
        length(inputDirection.x, inputDirection.z) > 0
          ? normalize(inputDirection.x + forward * 0.42, inputDirection.z)
          : { x: forward, z: Math.sin(player.heading) * 0.28 };
      this.kickBall(player, "through", direction, input.throughPower);
    }

    if (input.shoot && !this.previousInput.shoot && this.state.ball.controlledBy === player.id) {
      const goalDirection = player.team === "home" ? 1 : -1;
      this.kickBall(
        player,
        "shot",
        {
          x: goalDirection,
          z: clamp(-player.z / 28 + input.moveZ * 0.12, -0.42, 0.42)
        },
        input.shotPower
      );
    }

    if (input.skill && !this.previousInput.skill && this.state.ball.controlledBy === player.id) {
      const direction = normalize(Math.cos(player.heading), Math.sin(player.heading));
      this.state.ball.controlledBy = null;
      this.state.ball.lastTouch = player.id;
      this.state.ball.x = player.x + direction.x * 0.95;
      this.state.ball.z = player.z + direction.z * 0.95;
      this.state.ball.vx = player.vx + direction.x * 8.5;
      this.state.ball.vz = player.vz + direction.z * 8.5;
      this.state.ball.vy = 0.22;
      player.stamina = clamp(player.stamina - 0.045, 0.2, 1);
      player.action = "skill";
      player.actionNonce += 1;
      this.addEvent("skill", player.team, player.id, "Knock-on");
    }

    const standingTackle =
      input.tackle && !this.previousInput.tackle;
    const slideTackle =
      input.slide && !this.previousInput.slide;
    if (standingTackle || slideTackle) {
      const opponent = this.nearestOpponent(player);
      const reach = slideTackle ? 2.25 : 1.45;
      if (slideTackle) {
        player.vx += Math.cos(player.heading) * 2.8;
        player.vz += Math.sin(player.heading) * 2.8;
        player.stamina = clamp(player.stamina - 0.035, 0.2, 1);
      }
      player.action = "tackle";
      player.actionNonce += 1;
      if (opponent && this.distancePlayers(player, opponent) < reach) {
        this.incrementTeamStat(player.team, "Tackles");
        if (this.state.ball.controlledBy === opponent.id) {
          this.state.ball.controlledBy = null;
          this.state.ball.vx += Math.cos(player.heading) * 4.5;
          this.state.ball.vz += Math.sin(player.heading) * 4.5;
          this.addEvent("tackle", player.team, player.id, "Ball won");
        }
      }
    }
  }

  private kickBall(
    player: PlayerState,
    kind: "pass" | "through" | "shot",
    rawDirection: { x: number; z: number },
    rawPower = 0.5
  ): void {
    const direction = normalize(rawDirection.x, rawDirection.z);
    const power = clamp(rawPower, 0.18, 1);
    const speed =
      kind === "shot"
        ? 18 + power * 15
        : kind === "through"
          ? 12 + power * 8
          : 9 + power * 7;
    this.state.ball.controlledBy = null;
    this.state.ball.lastTouch = player.id;
    this.state.ball.x = player.x + direction.x * 0.75;
    this.state.ball.z = player.z + direction.z * 0.75;
    this.state.ball.y = BALL_RADIUS + 0.04;
    this.state.ball.vx = direction.x * speed;
    this.state.ball.vz = direction.z * speed;
    this.state.ball.vy =
      kind === "shot" ? 0.8 + power * 3.2 : kind === "through" ? 0.18 + power * 1.4 : 0.18;
    this.state.ball.angularVelocityX = direction.z * speed;
    this.state.ball.angularVelocityZ = -direction.x * speed;
    player.action = "kick";
    player.actionNonce += 1;

    if (kind === "shot") {
      this.incrementTeamStat(player.team, "Shots");
      if (Math.abs(this.state.ball.z) < PITCH.goalWidth / 2) {
        this.incrementTeamStat(player.team, "ShotsOnTarget");
      }
      this.addEvent("shot", player.team, player.id, "Shot");
    } else {
      this.incrementTeamStat(player.team, "Passes");
      this.addEvent(
        kind === "through" ? "through" : "pass",
        player.team,
        player.id,
        kind === "through" ? "Through ball" : "Pass"
      );
    }
  }

  private updateBall(dt: number): void {
    const owner = this.state.ball.controlledBy ? this.player(this.state.ball.controlledBy) : null;
    if (owner) {
      const targetX = owner.x + Math.cos(owner.heading) * 0.82;
      const targetZ = owner.z + Math.sin(owner.heading) * 0.82;
      const spring = 32;
      this.state.ball.vx += (targetX - this.state.ball.x) * spring * dt;
      this.state.ball.vz += (targetZ - this.state.ball.z) * spring * dt;
      this.state.ball.vx *= Math.pow(0.08, dt);
      this.state.ball.vz *= Math.pow(0.08, dt);
      this.state.ball.y = BALL_RADIUS;
      this.state.ball.vy = 0;

      const displacement = length(targetX - this.state.ball.x, targetZ - this.state.ball.z);
      if (displacement > 2.4) this.state.ball.controlledBy = null;
      this.incrementPossession(owner.team);
    } else {
      this.state.ball.vy -= 9.81 * dt;
      const drag = Math.pow(0.994, dt * 60);
      this.state.ball.vx *= drag;
      this.state.ball.vz *= drag;
    }

    this.state.ball.x += this.state.ball.vx * dt;
    this.state.ball.y += this.state.ball.vy * dt;
    this.state.ball.z += this.state.ball.vz * dt;

    if (this.state.ball.y < BALL_RADIUS) {
      this.state.ball.y = BALL_RADIUS;
      if (Math.abs(this.state.ball.vy) > 0.7) {
        this.state.ball.vy = -this.state.ball.vy * 0.46;
      } else {
        this.state.ball.vy = 0;
      }
      const rollingResistance = Math.pow(0.975, dt * 60);
      this.state.ball.vx *= rollingResistance;
      this.state.ball.vz *= rollingResistance;
    }

    if (Math.abs(this.state.ball.z) > PITCH.halfWidth) {
      this.state.ball.z = clamp(this.state.ball.z, -PITCH.halfWidth, PITCH.halfWidth);
      this.state.ball.vz *= -0.25;
      this.state.ball.controlledBy = null;
    }
    if (Math.abs(this.state.ball.x) > PITCH.halfLength + 3) {
      this.state.ball.x = clamp(this.state.ball.x, -PITCH.halfLength - 3, PITCH.halfLength + 3);
      this.state.ball.vx *= -0.25;
    }
  }

  private resolvePossession(): void {
    if (this.state.ball.controlledBy || this.state.ball.y > 0.55) return;
    if (length(this.state.ball.vx, this.state.ball.vz) > 9) return;

    let winner: PlayerState | null = null;
    let winnerDistance = Number.POSITIVE_INFINITY;
    for (const player of this.state.players) {
      const distance = length(player.x - this.state.ball.x, player.z - this.state.ball.z);
      if (distance < PLAYER_CONTROL_RADIUS && distance < winnerDistance) {
        winner = player;
        winnerDistance = distance;
      }
    }

    if (winner) {
      this.state.ball.controlledBy = winner.id;
      this.state.ball.lastTouch = winner.id;
      if (winner.team === "home") this.state.activePlayerId = winner.id;
    }
  }

  private checkGoal(): void {
    if (Math.abs(this.state.ball.x) <= PITCH.halfLength) return;
    if (Math.abs(this.state.ball.z) > PITCH.goalWidth / 2) return;
    if (this.state.ball.y > PITCH.goalHeight) return;

    const scoringTeam: TeamId = this.state.ball.x > 0 ? "home" : "away";
    if (scoringTeam === "home") this.state.homeScore += 1;
    else this.state.awayScore += 1;
    const scorer = this.state.ball.lastTouch;
    this.addEvent("goal", scoringTeam, scorer, "GOAL");
    this.resetKickoff(scoringTeam === "home" ? "away" : "home");
  }

  private resetKickoff(team: TeamId): void {
    for (const player of this.state.players) {
      const formationId =
        player.team === "home"
          ? this.state.config.homeFormation ?? "4-3-3"
          : this.state.config.awayFormation ?? "4-3-3";
      const anchor = formationAnchor(player, formationId);
      player.x = anchor.x;
      player.z = anchor.z;
      player.vx = 0;
      player.vz = 0;
      player.heading = player.team === "home" ? 0 : Math.PI;
      player.action = "idle";
      player.actionNonce += 1;
    }
    this.state.activePlayerId = team === "home" ? "home-10" : "home-8";
    this.state.ball = freshBall();
    this.state.ball.controlledBy = `${team}-10`;
    this.state.ball.lastTouch = `${team}-10`;
  }

  private switchActivePlayer(): void {
    const candidates = this.state.players
      .filter((player) => player.team === "home")
      .sort(
        (a, b) =>
          length(a.x - this.state.ball.x, a.z - this.state.ball.z) -
          length(b.x - this.state.ball.x, b.z - this.state.ball.z)
      );
    const next = candidates.find((player) => player.id !== this.state.activePlayerId) ?? candidates[0];
    if (next) this.state.activePlayerId = next.id;
  }

  private nearestPlayersToBall(team: TeamId, count: number): PlayerState[] {
    return this.state.players
      .filter((player) => player.team === team && player.role !== "GK")
      .sort(
        (a, b) =>
          length(a.x - this.state.ball.x, a.z - this.state.ball.z) -
          length(b.x - this.state.ball.x, b.z - this.state.ball.z)
      )
      .slice(0, count);
  }

  private nearestOpponent(player: PlayerState): PlayerState | null {
    let nearest: PlayerState | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const candidate of this.state.players) {
      if (candidate.team === player.team) continue;
      const distance = this.distancePlayers(player, candidate);
      if (distance < nearestDistance) {
        nearest = candidate;
        nearestDistance = distance;
      }
    }
    return nearest;
  }

  private player(id: string): PlayerState | null {
    return this.state.players.find((player) => player.id === id) ?? null;
  }

  private distancePlayers(a: PlayerState, b: PlayerState): number {
    return length(a.x - b.x, a.z - b.z);
  }

  private incrementPossession(team: TeamId): void {
    if (team === "home") this.state.stats.homePossessionTicks += 1;
    else this.state.stats.awayPossessionTicks += 1;
  }

  private incrementTeamStat(
    team: TeamId,
    stat: "Shots" | "ShotsOnTarget" | "Passes" | "Tackles"
  ): void {
    const prefix = team === "home" ? "home" : "away";
    const key = `${prefix}${stat}` as keyof MatchSnapshot["stats"];
    this.state.stats[key] += 1;
  }

  private addEvent(
    kind: MatchEvent["kind"],
    team: TeamId | null,
    playerId: string | null,
    label: string
  ): void {
    this.state.eventNonce += 1;
    this.state.events.push({
      id: this.state.eventNonce,
      minute: Math.min(90, Math.floor(this.state.clockSeconds / 60)),
      kind,
      team,
      playerId,
      label
    });
    this.state.events = this.state.events.slice(-24);
  }
}
